<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">
        Save As Profile
      </p>
      <a class="close-modal" aria-label="close" @click="closeModal()">
        <i class="material-icons">close</i>
      </a>
    </header>
    <section class="modal-card-body">

      <p>
        You can save a profile, if you'd like. But you need to give it a name.
      </p>

      <h5>
        New Profile Name
      </h5>

      <input type="text" class="input" placeholder="Enter profile name..." v-model="profileName">

      <div class="warning-box" v-if="profileNameExists">
        <i class="material-icons">warning</i> Saving a profile with this name will over-write the existing profile with this name. To save a new copy, change the name above.
      </div>

      <div class="asof-type">
        <h5>Date</h5>
        <div class="pseudo-selector"
            v-for="(type, copy) of asOfOptions"
            :key="type"
            :class="{'pseudo-selector--active': asOfType === type }"
            @click="asOfType = type"
        >
          <div class="pseudo-selector__radio"></div>
          Always use {{ copy }}
        </div>
      </div>

      <div class="buttons-row">
        <button class="button button-secondary button-outline button-medium"
          @click="closeModal()"
        >
          Cancel
        </button>
        <button class="button button-primary button-medium"
          :disabled="!profileName || saveLoading"
          @click="saveModal()"
        >
          Save
        </button>
      </div>

    </section>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import GAService from '@/services/ga.service';
import { AsOf, AsOfType, AsOfData } from '../../store/types';
import moment from 'moment';

@Component({
  components: {
  },
})
export default class SaveAsProfileModal extends Vue {
  public asOfType: AsOfType = AsOfType.Relative;
  public profileName: string = '';
  public saveLoading: boolean = false;

  public get asOfDate(): Date {
    return this.$store.getters.getAsOfDate;
  }

  public get asOfRelative(): AsOfData {
    return this.$store.getters.getAsOfRelative;
  }

  public get asOfOptions(): object {
    const staticKey: string = moment(this.asOfDate).format('DD/MM/YYYY');
    const { count, unit }: AsOfData = this.asOfRelative;

    return {
      [staticKey]: AsOfType.Static,
      [`${count} ${unit}${count > 1 ? 's': ''} ago`]: AsOfType.Relative
    };
  }

  get profileToSave() {
    return this.$store.state.profiles.profileToSave;
  }

  get profiles() {
    return this.$store.state.profiles.profiles || [];
  }

  get profileNameExists() {
    if (this.profiles && this.profileToSave) {
      return this.profiles.find((profile: any) => {
        return profile.name === this.profileName;
      });
    }

    return false;
  }

  public closeModal() {
    this.$store.commit('setModal', '');
  }

  public async saveModal() {
    this.saveLoading = true;
    let asOf:AsOf = this.asOfType === AsOfType.Relative ?
      { type: AsOfType.Relative, data: this.asOfRelative } :
      { type: AsOfType.Static, data: this.asOfDate.toISOString().slice(0, 10) };

    const profile = { ...this.profileToSave, asOf, name: this.profileName  };

    if (this.profileNameExists) {
      this.overwriteProfile();
    } else {
      try {
        GAService.sendEvent(this.$gtag, 'Profile', 'Saved', profile.name);
        await this.$store.dispatch('saveProfile', profile);
        this.$store.commit('setToastSuccess', 'Profile saved!');
        this.closeModal();
        this.saveLoading = false;
      } catch (e) {
        this.$store.commit('setToastError', 'Error saving profile');
        this.saveLoading = false;
      }
    }
  }

  public async overwriteProfile() {
    try {
      GAService.sendEvent(this.$gtag, 'Profile', 'Overwritten', this.profileToSave.name);
      await this.$store.dispatch('replaceProfile', this.profileToSave);
      this.$store.commit('setToastSuccess', 'Profile saved!');
      this.closeModal();
      this.saveLoading = false;
    } catch (e) {
      this.$store.commit('setToastError', 'Error saving profile');
      this.saveLoading = false;
    }
  }

  public mounted() {
    if (!!this.profileToSave.name) {
      this.profileName = this.profileToSave.name;
    }

    const asOf: AsOf = this.$store.getters.getAsOf;
    this.asOfType = asOf.type;
  }

}

</script>

<style scoped lang="scss">

.input {
  margin-top: 8px;
}

.warning-box {
  margin-top: 15px;
  text-align: left;
  background-color: rgba(255, 204, 128, .45);
  border: 2px solid rgba(255, 204, 128, 1);
  padding: 3px 5px;
  border-radius: 4px;

  i {
    position: relative;
    top: 4px;
    color: #d89d44;
  }
}

.asof-type {
  h5 {
    margin-top: 8px;
  }

  .pseudo-selector {
    border-bottom: none;

    &:hover {
      background-color: inherit;
    }

    .pseudo-selector__radio {
      top: 47%;
      transform: translateY(-50%);
    }
  }
}

</style>
